import { calculateWindowSize } from '@/common/utils/helpers';
import {
    NAVBAR_DARK_VARIANTS,
    NAVBAR_LIGHT_VARIANTS,
    SCREEN_SIZE_TYPE,
    SIDEBAR_DARK_SKINS,
    SIDEBAR_LIGHT_SKINS
} from '@/modules/ui/types';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

const isDarkMode = (): boolean => process.env.VUE_APP_DARK_MODE.toLowerCase() === 'true';
const defaultNavbarVariant = (darkMode: boolean): string =>
    darkMode ? NAVBAR_DARK_VARIANTS[0].value : NAVBAR_LIGHT_VARIANTS[0].value;
const defaultSidebarSkin = (darkMode: boolean): string =>
    darkMode ? SIDEBAR_DARK_SKINS[0].value : SIDEBAR_LIGHT_SKINS[0].value;

export const useUiStore = defineStore(
    'store.ui',
    () => {
        const darkMode = ref(isDarkMode());
        const navbarVariant = ref(process.env.VUE_APP_NAVBAR_VARIANT || defaultNavbarVariant(darkMode.value));
        const sidebarSkin = ref(process.env.VUE_APP_SIDEBAR_SKIN || defaultSidebarSkin(darkMode.value));
        const menuSidebarCollapsed = ref(false);
        const controlSidebarCollapsed = ref(true);
        const screenSize = ref<SCREEN_SIZE_TYPE>(calculateWindowSize(window.innerWidth));
        const loadingCount = ref(0);
        const isLoading = computed(() => loadingCount.value > 0);

        const toggleDarkMode = (): void => {
            darkMode.value = !darkMode.value;
            navbarVariant.value = defaultNavbarVariant(darkMode.value);
            sidebarSkin.value = defaultSidebarSkin(darkMode.value);
        };

        const toggleMenuSidebar = (): void => {
            menuSidebarCollapsed.value = !menuSidebarCollapsed.value;
        };

        const toggleControlSidebar = (): void => {
            controlSidebarCollapsed.value = !controlSidebarCollapsed.value;
        };

        const setScreenSize = (value: SCREEN_SIZE_TYPE): void => {
            screenSize.value = value;
        };

        const setNavbarVariant = (value?: string): void => {
            navbarVariant.value = value || defaultNavbarVariant(darkMode.value);
        };

        const setSidebarSkin = (value?: string): void => {
            sidebarSkin.value = value || defaultSidebarSkin(darkMode.value);
        };

        const beginLoading = (): void => {
            loadingCount.value++;
        };

        const endLoading = (): void => {
            if (loadingCount.value > 0) {
                loadingCount.value--;
            }
        };

        const resetLoading = (): void => {
            loadingCount.value = 0;
        };

        return {
            darkMode,
            navbarVariant,
            sidebarSkin,
            menuSidebarCollapsed,
            controlSidebarCollapsed,
            screenSize,
            isLoading,
            loadingCount,
            toggleDarkMode,
            toggleMenuSidebar,
            toggleControlSidebar,
            setScreenSize,
            setNavbarVariant,
            setSidebarSkin,
            beginLoading,
            endLoading,
            resetLoading
        };
    },
    {
        persist: true
    }
);
